/**
 * `<caos-button>` — a single action. (Catalog: Actions › Button — a single action.)
 *
 * Catalog variants: primary · secondary · ghost · destructive · busy.
 * `busy` is a state (spinner + non-interactive), expressed by the `loading`
 * attribute rather than a colour variant, so a busy primary and a busy secondary
 * both read as busy. `soft` (the accent-soft fill, design system §04) is the fifth
 * colour variant. Radius, colours, and the focus ring all read `--caos-*` tokens.
 *
 * CAOS-599 — this is the ONE button. Three shapes that are commonly shipped as three more
 * components are a mode and a state on this one:
 *
 * - ICON-ONLY is a mode, not a second component. The label is a slot rather than a required
 *   attribute, so a button with a glyph and no text is this element with nothing slotted into it.
 *   It draws square and takes its accessible name from `label`, because there is no text to read.
 * - PRESSED is a state, not a second component, and it sits beside the busy state that was already
 *   here. It mirrors `aria-pressed` exactly: absent means this is not a toggle at all, `false`
 *   means a toggle that is off, and `true` means one that is on. One attribute carrying both facts
 *   is what lets a toggle need a single label instead of one for each state.
 * - SIZE now comes from the platform scale (`src/sizes.ts`) rather than from literals this
 *   component invented. The numbers do not move — the scale was drawn FROM them — but a theme can
 *   now change a control height once and have every control follow, and two buttons in a joined
 *   group cannot disagree about how tall they are.
 *
 * Styling seams: `::part(button)` (the control), `::part(spinner)` (the busy indicator) and
 * `::part(icon)` (the glyph); the label is the default slot. `--caos-button-radius` overrides the
 * corner radius, which is how `<caos-button-group>` squares off the buttons it joins.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { controlRules, type ControlStep } from '../sizes.js';
import { isIconName, makeIcon } from './icons.js';

export type CaosButtonVariant = 'primary' | 'secondary' | 'soft' | 'ghost' | 'destructive';
export type CaosButtonSize = ControlStep;

/** The step a button draws at when `size` is absent or names no step. */
export const BUTTON_DEFAULT_SIZE: ControlStep = 'md';

/** The button’s own styles, as written. */
export const BUTTON_STYLES = `
  :host {
    display: inline-flex;
    vertical-align: middle;
  }
  :host([block]) { display: flex; }

  button {
    /* geometry — the platform radius, and the room the size scale gives the label */
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--caos-space-2);
    width: 100%;
    /* The group squares off the corners it joins by setting these on the button; a button that is
       not in one never sees either property and takes the platform radius on all four corners.
       They are LOGICAL corners — start and end, not left and right — so a joined run reads correctly
       in a right-to-left script without the group having to know which way round it is. */
    border-start-start-radius: var(--caos-button-radius-start, var(--caos-radius));
    border-end-start-radius: var(--caos-button-radius-start, var(--caos-radius));
    border-start-end-radius: var(--caos-button-radius-end, var(--caos-radius));
    border-end-end-radius: var(--caos-button-radius-end, var(--caos-radius));
    border: 1px solid transparent;
    /* type — the display face, UPPERCASE, .14em tracking */
    font-family: var(--caos-font-display);
    font-weight: 600; /* button emphasis — off-scale weight, stays literal */
    letter-spacing: 0.14em;
    text-transform: uppercase;
    line-height: 1;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease, box-shadow 0.12s ease;
    /* default (secondary) colours — transparent fill + emphasised hairline */
    background: transparent;
    color: var(--caos-color-text);
    border-color: var(--caos-color-border-strong);
  }

  /* sizes — the platform scale, not numbers this component keeps to itself */
  ${controlRules(BUTTON_DEFAULT_SIZE, (host, l) => `${host} button { min-height: ${l.height}; padding: ${l.padding}; font-size: ${l.font}; }`)}

  /* An icon-only button is square: the same height, and that height as its width, so a row of them
     lines up on both axes. The padding goes because there is no label for it to hold off. */
  :host([icon-only]) button { padding: 0; width: 100%; aspect-ratio: 1; }
  /* CAOS-1670 — block and icon-only ask for different things and an author can set both. The
     square is the one that gives: honouring aspect-ratio: 1 at the container width makes the
     button as tall as the panel is wide. So width follows the author block attribute and the
     height stays on the control scale above, which is where every other button height comes from.
     Stated as a rule rather than left to the cascade, deliberately. Until now a single-attribute
     icon-only display rule sat here, and the only thing it did was cancel the block rule by being
     declared after it at the same specificity — inline-flex is already the default on :host. That
     made a combination this component cares about depend on the order two rules happen to appear
     in, so grouping the host rules while tidying the sheet would have silently reintroduced the
     full-width square. Two attributes beat one, so this holds wherever it is written. */
  :host([icon-only][block]) button { aspect-ratio: auto; }
  .icon { display: inline-flex; flex: none; width: 1.25em; height: 1.25em; }
  .icon svg { display: block; width: 100%; height: 100%; }
  .icon:empty { display: none; }
  /* The glyph leads by default and follows when the author says so. Nothing else about the button
     changes with the position — it is one flex row read in one direction or the other. */
  :host([icon-position="end"]) button { flex-direction: row-reverse; }

  /* variants */
  /* CAOS-1551 — accent-fill, not accent, because this paints white text at normal size and
     white on accent is 3.60:1 in light and 2.91:1 in dark, under the 4.5:1 AA floor. Both fill
     tokens darken the brand accent and are theme-INVARIANT: lightening for dark is what made the
     dark button the worse of the two. See the token descriptions for the per-brand measurements. */
  :host([variant="primary"]) button {
    background: var(--caos-color-accent-fill);
    border-color: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
  }
  :host([variant="primary"]) button:hover:not(:disabled) {
    background: var(--caos-color-accent-fill-hover);
    border-color: var(--caos-color-accent-fill-hover);
    /* subtle accent glow on hover */
    box-shadow: 0 0 0 4px color-mix(in srgb, var(--caos-color-accent) 18%, transparent);
  }
  :host([variant="soft"]) button {
    background: var(--caos-color-accent-soft);
    border-color: transparent;
    color: var(--caos-color-accent-strong);
  }
  :host([variant="soft"]) button:hover:not(:disabled) { border-color: var(--caos-color-accent); }

  /* secondary (and the no-variant default) — hover fills 10% accent, border → accent */
  :host([variant="secondary"]) button:hover:not(:disabled),
  :host(:not([variant])) button:hover:not(:disabled) {
    background: var(--caos-state-hover-bg);
    border-color: var(--caos-color-accent);
    color: var(--caos-color-text);
  }

  :host([variant="ghost"]) button {
    background: transparent;
    border-color: var(--caos-color-border);
    color: var(--caos-color-text-muted);
  }
  :host([variant="ghost"]) button:hover:not(:disabled) {
    background: transparent;
    border-color: var(--caos-color-border-strong);
    color: var(--caos-color-text);
  }

  :host([variant="destructive"]) button {
    background: var(--caos-color-surface);
    border-color: color-mix(in srgb, var(--caos-color-danger) 45%, var(--caos-color-border));
    color: var(--caos-color-danger);
  }
  /* CAOS-1551 — danger-fill, not danger. Light danger is 4.99:1 under white and passes; the
     DARK danger is lightened to #E5675C, where the same white label measures 3.27:1. This state
     is a hover, which is the least likely place for anyone to notice. */
  :host([variant="destructive"]) button:hover:not(:disabled) {
    background: var(--caos-color-danger-fill);
    border-color: var(--caos-color-danger-fill);
    color: var(--caos-color-danger-contrast);
  }

  /* the press of a pointer, which is not the same thing as the pressed STATE below */
  button:active:not(:disabled) { transform: translateY(0.5px); }

  /* PRESSED — a toggle that is on. It is drawn as the selected state every other component in the
     library uses for "this one is in force", rather than as a sixth colour variant, so a pressed
     secondary and a pressed ghost read the same way as a selected row does. */
  :host([pressed="true"]) button,
  :host([pressed=""]) button {
    background: var(--caos-state-selected-bg);
    border-color: var(--caos-color-accent);
    color: var(--caos-color-accent-strong);
  }
  :host([pressed="true"][variant="primary"]) button,
  :host([pressed=""][variant="primary"]) button {
    background: var(--caos-color-accent-fill-hover);
    border-color: var(--caos-color-accent-fill-hover);
    color: var(--caos-color-accent-contrast);
  }

  /* focus-visible — the accent ring (design system focus spec) */
  button:focus-visible {
    outline: none;
    box-shadow: var(--caos-focus-ring);
    border-color: var(--caos-color-accent);
  }

  /* disabled */
  button:disabled { opacity: var(--caos-state-disabled-opacity); cursor: not-allowed; }

  /* busy / loading */
  :host([loading]) button { cursor: progress; color: transparent; }
  .spinner {
    display: none;
    position: absolute;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid currentColor;
    border-top-color: transparent;
    animation: caos-spin 0.6s linear infinite;
  }
  :host([loading]) .spinner { display: block; }
  /* keep the spinner in the button's text colour, not the transparented label colour */
  :host([loading][variant="primary"]) .spinner { color: var(--caos-color-accent-contrast); }
  :host([loading][variant="destructive"]) button:hover .spinner { color: var(--caos-color-danger-contrast); }
  :host([loading]:not([variant="primary"])) .spinner { color: var(--caos-color-accent); }

  @keyframes caos-spin { to { transform: rotate(360deg); } }

  button { position: relative; }
`;

const sheet = makeSheet(BUTTON_STYLES);

export class CaosButton extends CaosElement {
  // Form-associated so `<caos-button type="submit">` can drive the <form> it sits in. The real
  // control lives in this element's shadow root, and native form submission does NOT cross a
  // shadow boundary — a shadow submit button cannot submit a light-DOM form. As a form-associated
  // element we hold `ElementInternals` (which exposes the associated `form`) and forward the click
  // to it, so a submit/reset button behaves like a native one (CAOS-206).
  static formAssociated = true;

  static get observedAttributes(): string[] {
    return ['disabled', 'loading', 'type', 'icon', 'pressed', 'label'];
  }

  private button: HTMLButtonElement;
  private internals: ElementInternals | null;
  private readonly iconBox: HTMLSpanElement;
  private readonly labelSlot: HTMLSlotElement;

  constructor() {
    super(sheet);
    // `attachInternals` is what exposes the associated form. Guard it so a DOM environment that
    // does not implement ElementInternals (some test/SSR shims) degrades to a plain button rather
    // than throwing in the constructor; the real browser has it, so form submission still works.
    try {
      this.internals = this.attachInternals();
    } catch {
      this.internals = null;
    }
    this.button = document.createElement('button');
    this.button.setAttribute('part', 'button');
    const spinner = document.createElement('span');
    spinner.className = 'spinner';
    spinner.setAttribute('part', 'spinner');
    spinner.setAttribute('aria-hidden', 'true');
    this.iconBox = document.createElement('span');
    this.iconBox.className = 'icon';
    this.iconBox.setAttribute('part', 'icon');
    this.iconBox.setAttribute('aria-hidden', 'true');
    this.labelSlot = document.createElement('slot');
    // The slot tells us whether there is a label, and it is the only thing that can: an author may
    // slot the label in at any point after the element is created, and a page assembled from stored
    // metadata always does it afterwards. So the mode follows the slot rather than being decided
    // once in the constructor.
    this.labelSlot.addEventListener('slotchange', () => this.syncMode());
    this.button.append(spinner, this.iconBox, this.labelSlot);
    this.root.append(this.button);
    // Forward the intent of a submit/reset button to the associated form (the inner shadow button
    // cannot do this natively). A `button`/unset type stays inert, so non-form buttons (CTAs, SSO,
    // buttons with their own click handlers) are unaffected.
    this.button.addEventListener('click', () => this.activateFormAction());
  }

  /** If this is a submit/reset button in a form, request that action on the associated form. */
  private activateFormAction(): void {
    if (this.hasAttribute('disabled') || this.hasAttribute('loading')) return;
    const type = this.getAttribute('type') ?? 'button';
    const form = this.internals?.form;
    if (!form) return;
    if (type === 'submit') form.requestSubmit();
    else if (type === 'reset') form.reset();
  }

  connectedCallback(): void {
    this.sync();
    this.syncMode();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  /**
   * Whether this button is drawing a label. An icon-only button is this same element with nothing
   * slotted into it, so the question is asked of the slot rather than of an attribute an author
   * would have to remember to keep in step with the content.
   */
  private syncMode(): void {
    const assigned = this.labelSlot.assignedNodes({ flatten: true });
    const hasLabel = assigned.some((node) =>
      node.nodeType === Node.ELEMENT_NODE || (node.textContent ?? '').trim().length > 0,
    );
    this.toggleAttribute('icon-only', !hasLabel && this.iconBox.childElementCount > 0);
    this.nameIt();
  }

  /**
   * The accessible name, which is never allowed to contradict the words on screen.
   *
   * A button with only a glyph has no text to read, so `label` IS its name — and a glyph with no
   * name at all is announced as "button" and nothing else, which is why `label` is documented as
   * required in that mode rather than merely available.
   *
   * When there IS visible text, the visible text is the name. `label` may extend it — "Submit" on a
   * page with three of them becoming "Submit expense report" — and that is the useful case. What it
   * may not do is replace it: a control whose spoken name does not contain its visible label cannot
   * be operated by anyone using speech to drive it, who says what they can see. So a label that does
   * not contain the visible text is not applied, and the visible text stands.
   */
  private nameIt(): void {
    // Both sides are compared with their whitespace flattened. A label slotted in as markup arrives
    // with the newlines and indentation of whatever wrote it, and comparing those literally would
    // reject a label that does extend the words a reader sees — the useful case — on the strength
    // of a line break nobody can see.
    const flat = (text: string): string => text.replace(/\s+/g, ' ').trim().toLowerCase();
    const label = (this.getAttribute('label') ?? '').trim();
    const visible = flat(this.textContent ?? '');
    const safe = label.length > 0 && (visible.length === 0 || flat(label).includes(visible));
    if (safe) this.button.setAttribute('aria-label', label);
    else this.button.removeAttribute('aria-label');
  }

  private sync(): void {
    const disabled = this.hasAttribute('disabled') || this.hasAttribute('loading');
    this.button.disabled = disabled;
    this.button.type = (this.getAttribute('type') as 'button' | 'submit' | 'reset') ?? 'button';
    if (this.hasAttribute('loading')) {
      this.button.setAttribute('aria-busy', 'true');
    } else {
      this.button.removeAttribute('aria-busy');
    }
    this.drawIcon(); // which syncs the mode, and the name with it
    this.syncPressed();
  }

  /** The glyph, from the platform's one vocabulary. A name it does not have draws nothing. */
  private drawIcon(): void {
    const name = (this.getAttribute('icon') ?? '').trim();
    if (isIconName(name)) this.iconBox.replaceChildren(makeIcon(name));
    else this.iconBox.replaceChildren();
    this.syncMode();
  }

  /**
   * The toggle state, mirrored onto the control a screen reader actually reads.
   *
   * Absent means this is not a toggle and the control says nothing about being pressed. Present
   * means it is one, and `pressed="false"` is as load-bearing as `pressed="true"`: without it a
   * toggle that is off is indistinguishable from a button that never toggles.
   */
  private syncPressed(): void {
    if (!this.hasAttribute('pressed')) {
      this.button.removeAttribute('aria-pressed');
      return;
    }
    const value = (this.getAttribute('pressed') ?? '').trim().toLowerCase();
    this.button.setAttribute('aria-pressed', value === 'false' ? 'false' : 'true');
  }
}

define('caos-button', CaosButton);

declare global {
  interface HTMLElementTagNameMap {
    'caos-button': CaosButton;
  }
}
